import * as z from "zod/mini";

/**
 * @description
 * spwn としての言語記号のマスタ
 * かつ spwn としての言語記号と表記のマップ
 */
export const LOCALE_MASTER = {
  en_US: "English",
  es_ES: "Español",
  ja_JP: "日本語",
  ko_KR: "한국어",
  zh_CN: "简体中文",
  zh_TW: "繁體中文",
} as const;

type LocaleMaster = typeof LOCALE_MASTER;
export type SpwnLocaleKey = keyof LocaleMaster;
export type LanguageType = {
  [K in keyof LocaleMaster]: {
    label: LocaleMaster[K];
    value: K;
  };
}[keyof LocaleMaster];

export const DEFAULT_LOCALE: LanguageType = {
  label: "日本語",
  value: "ja_JP",
};

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
/**
 * @description
 * zod 側で const 型パラメータを使っており、 portal の tsconfig の設定上利用できない型定義
 * TODO: ts の設定が変わったら disable directive を外せるか確認
 */
const SpwnLocaleKeySchema = z.union<z.ZodMiniType<SpwnLocaleKey>[]>(
  // @ts-expect-error zod の 定義がパースできないため
  Object.keys(LOCALE_MASTER).map((key) => z.literal(key))
);
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */

/**
 * @description
 * z.union や z.literal が現時点の tsconfig の設定で解析できない型定義になっていて型エラーを起こすため
 * schema の使用毎に disable しなくて良いように SpwnLocaleKeySchema を薄くラップしてる
 * 挙動については test で担保してる
 */
export const parseAsSpwnLocaleKey = (input: unknown): SpwnLocaleKey => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    return SpwnLocaleKeySchema.parse(input) as SpwnLocaleKey;
  } catch {
    return DEFAULT_LOCALE.value;
  }
};

/**
 * @description
 * spwn の言語記号と各翻訳ツールで対応してる言語記号とのマップ用
 */
type MapToTool<T = string> = Record<SpwnLocaleKey, T>;

export type WeglotKey = "en" | "es" | "ja" | "ko" | "zh" | "zh-tw";

/**
 * @description
 * spwn としての言語記号と weglot で対応してる言語記号のマップ
 * weglot はサービス側で記号が定義済みかつ変更不可
 */
export const LOCALE_MAP_TO_WEGLOT: MapToTool<WeglotKey> = {
  en_US: "en",
  es_ES: "es",
  ja_JP: "ja",
  ko_KR: "ko",
  zh_CN: "zh",
  zh_TW: "zh-tw",
};

export type I18nKey = "en" | "es" | "ja" | "ko" | "zh_cn" | "zh_tw";

/**
 * @description
 * spwn として言語記号と i18n-ts で対応してる言語記号のマップ
 * i18n-ts は記号の定義はなく自前の定義によるが、「-」が使えない
 */
export const LOCALE_MAP_TO_I18N_TS: MapToTool<I18nKey> = {
  en_US: "en",
  es_ES: "es",
  ja_JP: "ja",
  ko_KR: "ko",
  zh_CN: "zh_cn",
  zh_TW: "zh_tw",
};

type GetLanguageListInput = {
  keys: readonly SpwnLocaleKey[];
};
type GetLanguageListOutput = LanguageType[];
type GetLanguageList = (input: GetLanguageListInput) => GetLanguageListOutput;
export const getLanguageList: GetLanguageList = (input) => {
  const { keys } = input;
  return keys.reduce((previousValue, key) => {
    // @ts-expect-error 愚直に定義する以外の回避策がないが、テストで結果を担保してる
    previousValue.push({ label: LOCALE_MASTER[key], value: key });
    return previousValue;
  }, []);
};

const LANGUAGE_KEYS_IN_DISPLAY_ORDER: SpwnLocaleKey[] = [
  "en_US",
  "ja_JP",
  "es_ES",
  "zh_CN",
  "zh_TW",
  "ko_KR",
];

export const LANG_LIST: LanguageType[] = getLanguageList({
  keys: LANGUAGE_KEYS_IN_DISPLAY_ORDER,
});
